import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useSigninMutation,
  useCurrentUserQuery,
  useLogoutMutation,
} from "../redux/services/account/accountApi";
import { toast } from "react-toastify";
import { APP_TYPE, PARENT_COMPANY, USER } from "../config";
import { getParentCompanyFromUser, normalizeParentCompanyId } from "../utils/authority";
import { useLocalStorage } from "./useLocalStorage";
import { PageLoading } from "../shared";
import { useConfig } from "./useConfig";
import { clearApiCache } from "../redux/api";
import PUBLIC_ROUTES from "@/constants/public.routes";
import { useDispatch } from "react-redux";
import { clearClientAuthState } from "../utils/socketAuth";
import { authReturnPath } from "../utils/authReturnPath";

const AuthContext = createContext({
  user: null,
  loading: true,
  logout: async () => {},
  login: async () => ({ success: false }),
  setUser: () => {},
  setParentCompany: () => {},
  isAuthenticated: false,
  initialized: false,
});

const withAccess = (user, access) => {
  if (!user) return user;
  return {
    ...user,
    access: access || user.access || null,
  };
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [initialized, setInitialized] = useState(false);
  const [user, setUser] = useLocalStorage(USER, null);
  const { cleanUpConfig } = useConfig();
  const [_parentCompany, setParentCompany] = useLocalStorage(
    PARENT_COMPANY,
    null
  );

  const [signinMutation, { isLoading: signinLoading }] = useSigninMutation();
  const [logoutMutation, { isLoading: logoutLoading }] = useLogoutMutation();

  // Skip current user when on public routes or no cached user
  const isPublicRoute = PUBLIC_ROUTES.some((path) =>
    location.pathname.startsWith(path)
  );
  const skipCurrentUser = isPublicRoute || !user;
  const isAuthDebugEnabled =
    typeof window !== "undefined" &&
    window.localStorage?.getItem("debug-auth") === "true";
  const debugAuthLog = (...args) => {
    if (isAuthDebugEnabled) {
      // eslint-disable-next-line no-console
      console.log("[auth-debug]", ...args);
    }
  };

  const {
    data: currentUser,
    isLoading: currentUserLoading,
    error: currentUserError,
  } = useCurrentUserQuery(undefined, {
    skip: !initialized || skipCurrentUser,
  });

  const [initializationLoading, setInitializationLoading] = useState(true);

  // Combine all loading states more efficiently
  const isLoading = useMemo(
    () =>
      signinLoading ||
      logoutLoading ||
      currentUserLoading ||
      initializationLoading,
    [signinLoading, logoutLoading, currentUserLoading, initializationLoading]
  );

  // Move isAuthenticated to useMemo
  const isAuthenticated = useMemo(() => !!user?.active, [user?.active]);

  // Optimize initialization effect
  useEffect(() => {
    const initialize = async () => {
      if (initialized) return; // Prevent multiple initializations

      try {
        setInitializationLoading(true);

        // If we're on a public route, initialize immediately
        if (skipCurrentUser) {
          setInitialized(true);
          return;
        }

        // For protected routes, wait for the current user query to complete
        // This will naturally complete when the query finishes (success or error)
        if (currentUserLoading) {
          // Wait for the loading to complete
          return; // Exit early, let the next effect run handle completion
        }

        setInitialized(true);
      } finally {
        setInitializationLoading(false);
      }
    };

    initialize();
    debugAuthLog("init", {
      initialized,
      skipCurrentUser,
      currentUserLoading,
      path: location.pathname,
    });
  }, [currentUserLoading, skipCurrentUser, initialized]);

  // Optimize current user effect
  useEffect(() => {
    if (!initialized || currentUserLoading) return;

    if (skipCurrentUser) {
      debugAuthLog("currentUser: skipped", {
        path: location.pathname,
        hasUser: !!user,
      });
      if (!user) {
        setParentCompany(null);
      }
      return;
    }

    if (currentUser) {
      const nextParentCompany = getParentCompanyFromUser(currentUser.user);
      const previousParentCompany = normalizeParentCompanyId(_parentCompany);

      setUser((currentValue) =>
        withAccess(currentUser.user, currentUser.access || currentValue?.access)
      );

      if (
        nextParentCompany &&
        nextParentCompany !== previousParentCompany
      ) {
        localStorage.removeItem(APP_TYPE);
      }

      setParentCompany(nextParentCompany);
    } else if (!skipCurrentUser) {
      setUser(null);
      setParentCompany(null);
    }

    if (currentUserError && !skipCurrentUser) {
      debugAuthLog("currentUserError", {
        path: location.pathname,
        skipCurrentUser,
        error: currentUserError,
      });
      setUser(null);
      setParentCompany(null);
      dispatch(clearApiCache());
    }
  }, [
    currentUser,
    currentUserLoading,
    currentUserError,
    initialized,
    skipCurrentUser,
    user,
    _parentCompany,
    location.pathname,
  ]);

  // Handle authentication state changes
  useEffect(() => {
    if (!initialized) return;

    const isLoginPage = location.pathname === "/login";
    const isPublicRoute = PUBLIC_ROUTES.some((path) =>
      location.pathname.startsWith(path)
    );

    // If we have a user and we're on login, send them where they intended to go.
    if (user && isLoginPage) {
      navigate(authReturnPath(location.state?.from), { replace: true });
      return;
    }

    // If we do NOT have a user and we're on a protected route, force a redirect.
    // Important: this must be reactive to `currentUserLoading` changes so we don't
    // get stuck on a protected URL after a hard reload or in-flight auth checks.
    if (!user && !isPublicRoute) {
      if (currentUserLoading) return;
      debugAuthLog("redirect -> /login", {
        path: location.pathname,
        currentUserLoading,
        hasUser: !!user,
      });
      navigate("/login", {
        replace: true,
        state: { from: location },
      });
    }
  }, [
    user,
    initialized,
    currentUserLoading,
    location.pathname,
    location.state,
    navigate,
  ]);

  const loginUser = async (credentials) => {
    try {
      const userData = await signinMutation({
        user: credentials,
      }).unwrap();

      if (userData.mfaRequired) {
        return {
          success: false,
          requiresMfa: true,
          challengeId: userData.challengeId,
          factor: userData.factor,
          maskedDestination: userData.maskedDestination,
          message: userData.message,
        };
      }

      if (userData.status === "success") {
        // Clear stale app type so API middleware uses tenant defaults until config loads.
        localStorage.removeItem(APP_TYPE);
        setUser(withAccess(userData.user, userData.access));
        setParentCompany(getParentCompanyFromUser(userData.user));
        return { success: true };
      }

      const errorMessage = userData.message || "Login failed";
      console.warn("Login failed:", errorMessage);
      return { success: false, error: errorMessage };
    } catch (err) {
      const errorMessage = err.data?.message || err.message || "Login failed";
      console.error("Login error:", err);

      // Provide more specific error messages
      let userFriendlyMessage = errorMessage;
      if (err.status === 401) {
        userFriendlyMessage =
          "Invalid email or password. Please check your credentials and try again.";
      } else if (err.status === 429) {
        userFriendlyMessage =
          "Too many login attempts. Please try again later.";
      } else if (err.status >= 500) {
        userFriendlyMessage = "Server error. Please try again later.";
      } else if (!navigator.onLine) {
        userFriendlyMessage =
          "No internet connection. Please check your network and try again.";
      }

      return {
        success: false,
        error: userFriendlyMessage,
        details: err,
      };
    }
  };

  const logoutUser = async () => {
    try {
      await logoutMutation().unwrap();
      toast.success("You have been logged out successfully.", {
        toastId: "logout-success",
      });
    } catch (err) {
      console.error("Logout error:", err);
      // Still show success message since we're clearing the session anyway
      toast.info("You have been logged out.", {
        toastId: "logout-info",
      });
    } finally {
      // Clear everything regardless of logout API success
      clearClientAuthState();
      setUser(null);
      setParentCompany(null);
      localStorage.removeItem(APP_TYPE);

      // Clear Redux state
      cleanUpConfig();
      dispatch(clearApiCache());

      // Navigate to login
      // Add `state` so version-update reload logic doesn't hijack logout.
      navigate("/login", {
        replace: true,
        state: { __skipVersionReload: true },
      });
    }
  };

  const value = useMemo(
    () => ({
      user,
      loading: isLoading,
      logout: logoutUser,
      login: loginUser,
      setUser,
      setParentCompany,
      isAuthenticated,
      initialized,
    }),
    [user, isAuthenticated, isLoading, initialized, setUser, setParentCompany]
  );

  // Only show loading on initial load
  if (!initialized && isLoading) {
    return <PageLoading />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
